import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

const TOKEN_KEY = 'auth-token';
const BASE_URL = `${environment.apiHost}/api/v1/reliability`;

export interface ReliabilityAdapter {
  adapterId?: number;
  adapterName: string;
  adapterType: string;
  environmentName?: string;
  baseUrl?: string;
  healthCheckUrl?: string;
  authType?: string;
  authUsername?: string;
  authPassword?: string;
  authToken?: string;
  authUsernameSet?: boolean;
  authPasswordSet?: boolean;
  authTokenSet?: boolean;
  checkIntervalSeconds?: number;
  timeoutSeconds?: number;
  enabled?: boolean;
  lastStatus?: string;
  lastCheckedAt?: string;
  checkTargetCount?: number;
  requiredCheckTargetCount?: number;
  enabledCheckTargetCount?: number;
  nextDueAt?: string;
}

export interface ReliabilityCheckTarget {
  checkTargetId?: number;
  adapterId?: number;
  adapterName?: string;
  checkName: string;
  checkMode: string;
  urlOrCommand?: string;
  httpMethod?: string;
  authType?: string;
  authUsername?: string;
  authPassword?: string;
  authToken?: string;
  authUsernameSet?: boolean;
  authPasswordSet?: boolean;
  authTokenSet?: boolean;
  timeoutSeconds?: number;
  expectedStatusCodes?: string;
  required?: boolean;
  enabled?: boolean;
  displayOrder?: number;
  latestStatus?: string;
  latestCheckedAt?: string;
  latestHttpStatusCode?: number;
  latestResponseTimeMs?: number;
  latestErrorMessage?: string;
}

export interface ReliabilityAlertRule {
  alertRuleId?: number;
  adapterType: string;
  checkType: string;
  severity: string;
  failureThreshold: number;
  timeWindowMinutes: number;
  notificationChannel: string;
  enabled?: boolean;
}

export interface ReliabilityRunbook {
  runbookId?: number;
  adapterType: string;
  incidentType: string;
  title: string;
  description?: string;
  steps?: string;
  severity?: string;
  enabled?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ReliabilityService {
  async dashboard(): Promise<any> {
    return this.get('/dashboard');
  }

  async adapters(): Promise<ReliabilityAdapter[]> {
    return this.get('/adapters');
  }

  async saveAdapter(adapter: ReliabilityAdapter): Promise<any> {
    return this.post('/adapters', adapter);
  }

  async deleteAdapter(adapterId: number): Promise<any> {
    return this.delete(`/adapters/${adapterId}`);
  }

  async runCheck(adapterId: number): Promise<any> {
    return this.post(`/adapters/${adapterId}/run-check`, {});
  }

  async checkTargets(adapterId: number): Promise<ReliabilityCheckTarget[]> {
    return this.get(`/adapters/${adapterId}/checks`);
  }

  async saveCheckTarget(adapterId: number, target: ReliabilityCheckTarget): Promise<any> {
    const id = target.checkTargetId;
    return id
      ? this.put(`/adapters/${adapterId}/checks/${id}`, target)
      : this.post(`/adapters/${adapterId}/checks`, target);
  }

  async deleteCheckTarget(adapterId: number, checkTargetId: number): Promise<any> {
    return this.delete(`/adapters/${adapterId}/checks/${checkTargetId}`);
  }

  async runCheckTarget(adapterId: number, checkTargetId: number): Promise<any> {
    return this.post(`/adapters/${adapterId}/checks/${checkTargetId}/run-check`, {});
  }

  async healthChecks(): Promise<any[]> {
    return this.get('/health-checks');
  }

  async incidents(filters: { status?: string; severity?: string; adapterType?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.adapterType) params.set('adapterType', filters.adapterType);
    const query = params.toString();
    return this.get(`/incidents${query ? `?${query}` : ''}`);
  }

  async incidentDetail(incidentId: number): Promise<any> {
    return this.get(`/incidents/${incidentId}`);
  }

  async incidentReport(incidentId: number): Promise<any> {
    return this.get(`/incidents/${incidentId}/report`);
  }

  async alertRules(): Promise<ReliabilityAlertRule[]> {
    return this.get('/alert-rules');
  }

  async saveAlertRule(rule: ReliabilityAlertRule): Promise<any> {
    return this.post('/alert-rules', rule);
  }

  async deleteAlertRule(alertRuleId: number): Promise<any> {
    return this.delete(`/alert-rules/${alertRuleId}`);
  }

  async runbooks(): Promise<ReliabilityRunbook[]> {
    return this.get('/runbooks');
  }

  async saveRunbook(runbook: ReliabilityRunbook): Promise<any> {
    return this.post('/runbooks', runbook);
  }

  async deleteRunbook(runbookId: number): Promise<any> {
    return this.delete(`/runbooks/${runbookId}`);
  }

  async bootstrapDefaults(baseUrl: string, environmentName: string): Promise<any> {
    return this.post('/bootstrap-defaults', { baseUrl, environmentName });
  }

  async acknowledge(incidentId: number): Promise<any> {
    return this.post(`/incidents/${incidentId}/acknowledge`, {});
  }

  async resolve(incidentId: number): Promise<any> {
    return this.post(`/incidents/${incidentId}/resolve`, {});
  }

  async assignIncident(incidentId: number, assignedTo: string): Promise<any> {
    return this.post(`/incidents/${incidentId}/assign`, { assignedTo });
  }

  async addIncidentComment(incidentId: number, comment: string): Promise<any> {
    return this.post(`/incidents/${incidentId}/comments`, { comment });
  }

  private async get(path: string): Promise<any> {
    const res = await fetch(`${BASE_URL}${path}`, { headers: this.headers() });
    return this.unwrap(res);
  }

  private async post(path: string, body: any): Promise<any> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify(body),
    });
    return this.unwrap(res);
  }

  private async put(path: string, body: any): Promise<any> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'PUT',
      headers: this.headers(true),
      body: JSON.stringify(body),
    });
    return this.unwrap(res);
  }

  private async delete(path: string): Promise<any> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'DELETE',
      headers: this.headers(),
    });
    return this.unwrap(res);
  }

  private headers(json = false): HeadersInit {
    const token = localStorage.getItem(TOKEN_KEY) ?? '';
    return {
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  private async unwrap(res: Response): Promise<any> {
    const json: any = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
    return json?.data ?? json;
  }
}
